import type { ClientMsg, ServerMsg, CreateRoomRequest, CreateRoomResponse } from './protocol'
import type { Move } from '../core/types'

// ── Event types ───────────────────────────────────────────────────────────────

/** All events emitted by RoomClient — server messages plus lifecycle events */
export type RoomEvent =
  | ServerMsg
  | { type: 'connected' }
  | { type: 'disconnected' }
  | { type: 'connectionChange'; status: 'connected' | 'reconnecting' | 'disconnected' }

type Handler<T extends RoomEvent['type']> = (msg: Extract<RoomEvent, { type: T }>) => void

/**
 * Manages the WebSocket connection to the game room.
 * Handles connect, send, receive, and automatic reconnection.
 */
export class RoomClient {
  private ws: WebSocket | null = null
  private wsUrl = ''
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectDelay = 1000
  private closed = false
  private handlers = new Map<string, Array<(e: RoomEvent) => void>>()

  constructor(private readonly workerUrl: string) {
    // Normalise — strip trailing slash so /api/... paths never double up
    this.workerUrl = workerUrl.replace(/\/$/, '')
  }

  // ── Static factory ──────────────────────────────────────────────────────────

  static async createRoom(workerUrl: string, req: CreateRoomRequest): Promise<CreateRoomResponse> {
    const res = await fetch(`${workerUrl}/api/room`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    })
    if (!res.ok) throw new Error(`Failed to create room (${res.status})`)
    return res.json() as Promise<CreateRoomResponse>
  }

  // ── Subscription ────────────────────────────────────────────────────────────

  on<T extends RoomEvent['type']>(type: T, handler: Handler<T>): void {
    if (!this.handlers.has(type)) this.handlers.set(type, [])
    this.handlers.get(type)!.push(handler as (e: RoomEvent) => void)
  }

  // ── Connection ──────────────────────────────────────────────────────────────

  connect(roomCode: string, playerName: string, playerIndex?: number): void {
    this.closed = false
    const base = this.workerUrl.replace(/^http/, 'ws')
    const params = new URLSearchParams({ playerName })
    if (playerIndex !== undefined) params.set('playerIndex', String(playerIndex))
    this.wsUrl = `${base}/api/room/${roomCode}/ws?${params}`
    this.openSocket()
  }

  disconnect(): void {
    this.closed = true
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.ws?.close()
    this.ws = null
    this.emit({ type: 'connectionChange', status: 'disconnected' })
  }

  // ── Send helpers ────────────────────────────────────────────────────────────

  sendMove(move: Move): void { this.send({ type: 'move', move }) }
  sendPendingMove(move: Move): void { this.send({ type: 'submit_pending_move', move }) }
  sendResolveRound(): void { this.send({ type: 'resolve_round' }) }
  sendStartGame(): void { this.send({ type: 'start_game' }) }
  sendRematchRequest(): void { this.send({ type: 'REMATCH_REQUEST' }) }
  sendReaction(emoji: string): void { this.send({ type: 'REACTION', emoji }) }
  sendConvertToBot(playerIndex: number): void { this.send({ type: 'CONVERT_TO_BOT', playerIndex }) }

  // ── Internals ───────────────────────────────────────────────────────────────

  private send(msg: ClientMsg): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg))
    }
  }

  private openSocket(): void {
    this.ws = new WebSocket(this.wsUrl)

    this.ws.onopen = () => {
      this.reconnectDelay = 1000
      this.emit({ type: 'connected' })
      this.emit({ type: 'connectionChange', status: 'connected' })
    }

    this.ws.onmessage = ({ data }) => {
      try {
        this.emit(JSON.parse(data as string) as RoomEvent)
      } catch { /* ignore malformed messages */ }
    }

    this.ws.onclose = () => {
      this.emit({ type: 'disconnected' })
      if (!this.closed) {
        this.emit({ type: 'connectionChange', status: 'reconnecting' })
        this.reconnectTimer = setTimeout(() => {
          this.reconnectDelay = Math.min(this.reconnectDelay * 2, 16000)
          this.openSocket()
        }, this.reconnectDelay)
      }
    }

    this.ws.onerror = () => { this.ws?.close() }
  }

  private emit(event: RoomEvent): void {
    // Snapshot handlers so that any on() calls during emit don't fire for this event
    const snapshot = [...(this.handlers.get(event.type) ?? [])]
    for (const h of snapshot) h(event)
  }
}
